/**
 * THE IDIOMA CLUB CONSTANTS
 * =====================
 * Centralized configuration and constants for the entire application.
 */

export interface AppConstants {
    APP_NAME: string;
    APP_VERSION: string;
    APP_ENV: 'development' | 'production';
    API: {
        SUPABASE_URL: string;
        SUPABASE_ANON_KEY: string;
        REQUEST_TIMEOUT: number;
    };
    STORAGE: {
        AUTH_TOKEN: string;
        PROFILE_CACHE: string;
        LESSONS_CACHE: string;
        CURRENT_WORLD: string;
        USER_DATA: string;
        THEME: string;
    };
    ROUTES: Record<string, string>;
    ROLES: {
        ADMIN: string;
        TEACHER: string;
        STUDENT: string;
    };
    STATUS: Record<string, string>;
    XP: Record<string, number>;
    LEAGUES: Array<{ name: string; minXP: number; color: string }>;
    UI: Record<string, number>;
    COLORS: Record<string, string>;
    AVATAR: {
        DEFAULT_URL: string;
        SEEDS: string[];
    };
    FEATURES: Record<string, boolean>;
}

const SC_CONSTANTS: AppConstants = {
    // === APPLICATION INFO ===
    APP_NAME: 'The Idioma Club',
    APP_VERSION: '2.0.0',
    APP_ENV: window.location.hostname === 'localhost' ? 'development' : 'production',

    // === API CONFIGURATION ===
    API: {
        SUPABASE_URL: import.meta.env.VITE_SUPABASE_URL as string,
        SUPABASE_ANON_KEY: import.meta.env.VITE_SUPABASE_ANON_KEY as string,
        REQUEST_TIMEOUT: 10000,
    },

    // === STORAGE KEYS ===
    STORAGE: {
        AUTH_TOKEN: 'sb-fryfcvxbdjikxsvdpnks-auth-token',
        PROFILE_CACHE: 'idiomaclub_profile_cache',
        LESSONS_CACHE: 'smart_lessons_cache',
        CURRENT_WORLD: 'idiomaclub_current_world',
        USER_DATA: 'idiomaClubUserData',
        THEME: 'idiomaclub_theme',
    },

    // === ROUTES ===
    ROUTES: {
        HOME: '/#map',
        MAP: '/#map',
        RANK: '/#rank',
        CLAN: '/#clan',
        PROFILE: '/#profile',
        ADMIN: '/#admin',
        LOGIN: '/#login',
        LESSON: '/#lesson',
    },

    // === USER ROLES ===
    ROLES: {
        ADMIN: 'admin',
        TEACHER: 'teacher',
        STUDENT: 'student',
    },

    // === USER STATUS ===
    STATUS: {
        ACTIVE: 'active',
        PENDING: 'pending',
        SUSPENDED: 'suspended',
    },

    // === GAMIFICATION ===
    XP: {
        LESSON_COMPLETE: 20,
        QUIZ_CORRECT: 10,
        SPEAKING_COMPLETE: 15,
        STREAK_BONUS: 5,
        DAILY_GOAL: 50,
    },

    // === LEAGUES ===
    LEAGUES: [
        { name: 'Bronce', minXP: 0, color: '#CD7F32' },
        { name: 'Plata', minXP: 500, color: '#C0C0C0' },
        { name: 'Oro', minXP: 1500, color: '#FFD700' },
        { name: 'Diamante', minXP: 3500, color: '#B9F2FF' },
        { name: 'Leyenda', minXP: 7000, color: 'var(--color-premium-accent)' },
    ],

    // === UI ===
    UI: {
        TRANSITION_DURATION: 200, // ms
        SKELETON_DELAY: 100, // ms
        TOAST_DURATION: 4000, // ms
        DEBOUNCE_DELAY: 300, // ms
        PREFETCH_DELAY: 100, // ms
    },

    // === COLORS ===
    COLORS: {
        PRIMARY: 'var(--sc-green)',
        NAVY: 'var(--sc-slate-900)',
        CORAL: 'var(--sc-red)',
        YELLOW: 'var(--sc-gold)',
        BACKGROUND: 'var(--sc-slate-50)',
    },

    // === AVATAR ===
    AVATAR: {
        DEFAULT_URL: 'https://api.dicebear.com/7.x/avataaars/svg?seed=',
        SEEDS: ['Felix', 'Luna', 'Max', 'Bella', 'Oliver', 'Mia', 'Leo', 'Sophie', 'Charlie', 'Coco'],
    },

    // === FEATURE FLAGS ===
    FEATURES: {
        ENABLE_SPEAKING: false,
        ENABLE_NOTIFICATIONS: true,
        ENABLE_DARK_MODE: false,
        ENABLE_OFFLINE: false,
        DEV_MODE: false,
    },
};

// Phase 6.1: Register via direct import (no more window.registry)
import { registry } from './registry';
registry.register('Constants', SC_CONSTANTS);

export default SC_CONSTANTS;
